import { useEffect, useRef, useState } from "react";
import logo from "../../logo.svg";
import { useNavigate, useLocation } from "react-router-dom";
import BadgeCartQuantity from "../BadgeCartQuantity/BadgeCartQuantity";
import { useLanguage } from './LanguageContext';
import { getDecodedTokenDetails } from "../Token/TokenService";
import { canAccessClientManagement, canAccessValidationPreordersButton } from "../Auth/AuthValidator";
import {useCheckUserUM} from "@/components/utils/useCheckUserUM";

function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();
    const [nav, setNav] = useState(false);
    const [role, setRole] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const { language, setLanguage } = useLanguage();
    const [deliveryNoteAccess, setDeliveryNoteAccess] = useState(false);
    const [userInitials, setUserInitials] = useState("");
    const [userMenu, setUserMenu] = useState(false);
    const [userNameIdentifier, setUserNameIdentifier] = useState("");
    const [atacFlowUser, setAtacFlowUser] = useState<boolean>(false);
    const [canAccessClientManagment, setClientManagmentAccess] = useState(false);
    const [canAccessValidationPreorders, setAccessToValidationPreorders] = useState(false);
    const [userUM, setUserUM] = useState<boolean | null>(null);

    useCheckUserUM((value: boolean) => setUserUM(value));

    const handleLanguageChange = (lang: string) => {
        setLanguage(lang);
    };

    function usePreviousLocation() {
        const location = useLocation();
        const previousLocationRef = useRef<typeof location>();

        useEffect(() => {
            previousLocationRef.current = location;
        }, [location]);

        return previousLocationRef.current;
    }

    const previousLocation = usePreviousLocation();

    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();

            if (tokenDetails?.token) {
                try {
                    setIsTokenValid(true);
                    const userRole = tokenDetails.userRole;
                    const userName = tokenDetails.userName;
                    const userIdent = tokenDetails.userIdent;
                    setUserNameIdentifier(userIdent);
                    setRole(userRole);

                    if (userRole === "AUCHANMBAEMPLOYEE") setDeliveryNoteAccess(true);
                    else setDeliveryNoteAccess(false);
                    await canAccessClientManagement() ? setClientManagmentAccess(true) : setClientManagmentAccess(false);
                    await canAccessValidationPreordersButton() ? setAccessToValidationPreorders(true) : setAccessToValidationPreorders(false);

                    const firstInitial = userName.split(" ")[0];
                    const secondInitial = userName.split(" ")[1];

                    const initials = firstInitial[0] + secondInitial[0];
                    setUserInitials(initials);

                    const userAtacFlow = tokenDetails.atacFlow;
                    setAtacFlowUser(userAtacFlow);
                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('refreshTokenExpiryTime');

        setRole('');
        setIsTokenValid(false);
        window.location.reload();
    };

    const handleBackButtonClick = () => {

        if (previousLocation?.pathname === '/login') {
            return;
        }
        navigate(-1);
    }

    const handleNav = () => {
        setNav(!nav);
    };

    const handleAccountNavigation = () => {
        navigate("/my-account");
    };

    const handleCartNavigation = () => {
        navigate("/my-cart");
        window.location.reload();
    };

    const showBackButton = location.pathname !== "/";

    const handleUserMenu = () => { setUserMenu(!userMenu); };

    return (
        <>
            <header className="bg-red-600">
                <div className="mx-auto flex h-16 max-w-screen-xl items-center gap-8 px-4 sm:px-6 lg:px-8">
                    {showBackButton && <button
                        type="button"
                        onClick={handleBackButtonClick}
                        className="flex items-center justify-center w-10 px-2 py-2 text-sm text-gray-700 transition-colors duration-200 bg-white border rounded-xl gap-x-2 sm:w-10 dark:hover:bg-gray-800 dark:bg-gray-900 hover:bg-gray-100 dark:text-gray-200 dark:border-gray-700"
                    >
                        <svg className="w-5 h-5 rtl:rotate-180" xmlns="http://www.w3.org/2000/svg" fill="none"
                            viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18" />
                        </svg>
                    </button>}
                    <a className="block text-white" href="/">
                        <span className="sr-only">Home</span>

                        <img src={logo} className="App-logo" alt="logo" />
                    </a>

                    <div className="flex flex-1 items-center justify-end md:justify-between">
                        <nav aria-label="Global" className="hidden md:block">
                            <ul className="flex items-center gap-6 text-sm">

                                {canAccessValidationPreorders && (<li>
                                    <a
                                        className="text-white transition hover:text-white/75"
                                        href="/validation/preorders"
                                    >
                                        {" "}
                                        Validare Precomenzi{" "}
                                    </a>
                                </li>)}

                                {/* <li>
                                      <a
                                        className="text-white transition hover:text-white/75"
                                        href="/"
                                      >
                                        {" "}
                                        Comanda Automata{" "}
                                      </a>
                                    </li> */}

                            </ul>
                        </nav>

                        <div className="flex items-center gap-4">
                            <div className="flex gap-4">
                                {!isTokenValid && <a
                                    className="block rounded-md bg-gray-100 px-5 py-2.5 text-sm font-medium text-red-600 transition hover:bg-rose-100"
                                    href="/login"
                                >
                                    Login
                                </a>}

                                {role === "BASIC" && <div className="text-2xl flex items-center space-x-5 text-white">
                                    <div onClick={handleCartNavigation} className="mr-2 cursor-pointer">
                                        <BadgeCartQuantity />
                                    </div>
                                </div>}

                                {isTokenValid && <div className="md:relative">
                                    <button
                                        type="button"
                                        onClick={handleUserMenu}
                                        className="overflow-hidden rounded-full border border-gray-300 shadow-inner"
                                    >
                                        <span className="sr-only">Toggle dashboard menu</span>

                                        <span
                                            className="grid size-10 place-content-center rounded-lg bg-gray-100 text-xs text-gray-600 ">
                                            {userInitials}
                                        </span>
                                    </button>


                                    {userMenu && <div
                                        className="absolute end-0 z-10 w-60 mt-3 divide-y divide-gray-100 rounded-md border border-gray-100 bg-white shadow-lg"
                                        role="menu"
                                    >
                                        <div className="p-2">
                                            <div
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700">Hello, {userNameIdentifier}
                                            </div>
                                        </div>

                                        <div className="p-2">
                                            {(role === "BASIC") && <div
                                                onClick={handleAccountNavigation}
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Profilul meu
                                            </div>}

                                            {!deliveryNoteAccess && <a
                                                href="/my-account/preorders"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Precomenzile mele
                                            </a>}

                                            {!deliveryNoteAccess && !atacFlowUser && <a
                                                href="/my-account/orders"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Comenzile mele
                                            </a>}

                                            {!deliveryNoteAccess && <a
                                                href="/my-account/invoices"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Facturile mele
                                            </a>}

                                            {!deliveryNoteAccess && <a
                                                href="/my-account/shipping-csv"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Livrarile mele
                                            </a>}

                                            {!deliveryNoteAccess && canAccessClientManagment && <a
                                                href="/my-account/return-orders"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Cerere retur
                                            </a>}

                                            {(role === "ADMIN" || role === "SUPERADMIN" || role === "B2BSUPPORT") && <a
                                                href="/my-account/cincu-prices"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Actualizare preturi
                                            </a>}

                                            {canAccessClientManagment && <a
                                                href="/client-management"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Client Management
                                            </a>}

                                            {deliveryNoteAccess && <a
                                                href="/my-account/delivery-note"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                Aviz de expeditie MBA
                                            </a>}

                                            {canAccessClientManagment && <a
                                                href="/user-management"
                                                className="block rounded-lg px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                                                role="menuitem"
                                            >
                                                User Management
                                            </a>}
                                        </div>

                                        <div className="p-2">
                                            <button
                                                type="submit"
                                                className="flex w-full items-center gap-2 rounded-lg px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                                                role="menuitem"
                                                onClick={handleLogout}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="currentColor"
                                                    className="size-4"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
                                                    />
                                                </svg>

                                                Logout
                                            </button>
                                        </div>
                                    </div>}
                                </div>}

                                {userUM && <div className="justify-center bg-red ml-3 rounded-lg hidden md:block">
                                    <div className="flex flex-row items-center right-1">
                                        <button
                                            onClick={() => handleLanguageChange("romana")}
                                            className={`p-2 flex flex-row items-center bg-white mr-1 border rounded-2xl border-gray-300 text-sm font-medium text-gray-700 hover:bg-grey-200
                                            focus:outline-none`}
                                            style={
                                                language === "romana"
                                                    ? { backgroundColor: 'rgb(252 231 243)' }
                                                    : undefined
                                            }
                                        >
                                            <span className="text-md">Ro</span>
                                            <span className="ml-1"> <img
                                                alt=""
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARMAAAC3CAMAAAAGjUrGAAAAG1BMVEUAK3/OESb80RYAI4F5b2f/1wn+1xXNACbdZyKTR8sdAAABAElEQVR4nO3QRwEAIAwEsDKLf8WouF8iIVUxa5+Zcl+PmFyJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEye5kw8yndcsBTMZHgAAAABJRU5ErkJggg=="
                                                className="w-5 h-5 rounded-xl" /></span>
                                        </button>

                                        <button
                                            onClick={() => handleLanguageChange("franceza")}
                                            className={`p-2 flex flex-row items-center bg-white border rounded-2xl border-gray-300 text-sm font-medium text-gray-700 hover:bg-grey-200
                                            focus:outline-none`}
                                            style={
                                                language === "franceza"
                                                    ? { backgroundColor: 'rgb(252 231 243)' }
                                                    : undefined
                                            }
                                        >
                                            <span className="text-md">Fr</span>
                                            <span className="ml-1"> <img
                                                alt=""
                                                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAP8AAACqCAMAAABVlWm8AAAACVBMVEUAJlT////OESaBoDijAAAAxklEQVR4nO3PMQEAAAjAILV/aFPsgwbMFLZwhaTv7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v75/+HwW0qVdTeFEnAAAAAElFTkSuQmCC"
                                                className="w-5 h-5 rounded-xl" /></span>
                                        </button>
                                    </div>
                                </div>}
                            </div>

                            {role === "ADMIN" && <button onClick={handleNav}
                                className="block rounded bg-gray-100 p-2.5 text-gray-600 transition hover:text-gray-600/75 md:hidden">
                                <span className="sr-only">Toggle menu</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                </svg>
                            </button>}
                            {nav &&
                                <ul
                                    className={
                                        nav
                                            ? 'fixed md:hidden left-0 top-0 w-[50%] h-full border-r border-r-red-800 bg-red-600 ease-in-out duration-500 z-50 '
                                            : 'ease-in-out w-[60%] duration-500 fixed top-0 bottom-0 left-[-100%]'
                                    }
                                >
                                    <li className=" text-center">
                                        <a className="block text-white text-center" href="/">
                                            <span className="sr-only">Home</span>

                                            <img src={logo} className="text-center p-6" alt="logo" />
                                        </a>
                                    </li>

                                    {(role !== "BASIC" && role !== "AUCHANMBAEMPLOYEE") && (<li className="mt-10 text-center">
                                        <a
                                            className="p-4 border-b rounded-xl bg-white duration-300 hover:text-black cursor-pointer border-red-800"
                                            href="/validation/preorders"
                                        >
                                            {" "}
                                            Validare Precomenzi {" "}
                                        </a>
                                    </li>)}

                                    {userUM && (<li className="mt-10 text-center">
                                        <div className="flex flex-row justify-center bg-red select-none ml-3 rounded-lg">
                                            <div className="flex flex-row items-center right-1">
                                                <button

                                                    className="p-2 flex flex-row items-center bg-white mr-1 border rounded-2xl border-gray-300 text-sm font-medium text-gray-700 hover:bg-red-300 focus:bg-red-300 focus:outline-none"
                                                >
                                                    <span className="text-md">Ro</span>
                                                    <span className="ml-1"> <img
                                                        alt=""
                                                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARMAAAC3CAMAAAAGjUrGAAAAG1BMVEUAK3/OESb80RYAI4F5b2f/1wn+1xXNACbdZyKTR8sdAAABAElEQVR4nO3QRwEAIAwEsDKLf8WouF8iIVUxa5+Zcl+PmFyJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEydOnDhx4sSJEye5kw8yndcsBTMZHgAAAABJRU5ErkJggg=="
                                                        className="w-5 h-5 rounded-xl" /></span>
                                                </button>

                                                <button

                                                    className="p-2 flex flex-row items-center bg-white border rounded-2xl border-gray-300 text-sm font-medium text-gray-700 hover:bg-red-300 focus:bg-red-300 focus:outline-none "
                                                >
                                                    <span className="text-md">Fr</span>
                                                    <span className="ml-1"> <img
                                                        alt=""
                                                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAP8AAACqCAMAAABVlWm8AAAACVBMVEUAJlT////OESaBoDijAAAAxklEQVR4nO3PMQEAAAjAILV/aFPsgwbMFLZwhaTv7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v7+/v75/+HwW0qVdTeFEnAAAAAElFTkSuQmCC"
                                                        className="w-5 h-5 rounded-xl" /></span>
                                                </button>
                                            </div>
                                        </div>
                                    </li>)}

                                    {/* <li className="mt-10 text-center">
                    <a
                      className="p-4 border-b rounded-xl bg-white duration-300 hover:text-black cursor-pointer border-red-800"
                      href="#"
                    >
                      {" "}
                      Comanda Automata {" "}
                    </a>
                  </li> */}

                                </ul>

                            }
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
}

export default Navbar;
